import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Lock, Mail, Eye, EyeOff, Sparkles, Clock } from 'lucide-react';
import { useSimpleAuth } from '../lib/supabase';
import { useRateLimit } from '../hooks/useRateLimit';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<{ remaining: number; resetAt?: string } | null>(null);
  const navigate = useNavigate();
  const { signIn, user, isAdmin } = useSimpleAuth();
  const { checkRateLimit, resetRateLimit } = useRateLimit();

  useEffect(() => {
    if (user && isAdmin) {
      navigate('/admin/dashboard');
    }
  }, [user, isAdmin, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!email || !password) {
        throw new Error('Please enter both email and password');
      }

      // Check rate limit before attempting login
      const rateLimitResult = await checkRateLimit('login', 5, 15);
      
      if (!rateLimitResult.allowed) {
        throw new Error(rateLimitResult.message || 'Too many login attempts. Please try again later.');
      }

      // Update rate limit info
      setRateLimitInfo({
        remaining: rateLimitResult.remaining,
        resetAt: rateLimitResult.resetAt
      });

      await signIn(email, password);
      
      // Reset rate limit on successful login
      await resetRateLimit('login');
      
      setSuccess(true);
      
      // Small delay to show success animation before navigating
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 1000);
    } catch (error: any) {
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const sparkleVariants = {
    animate: {
      scale: [1, 1.2, 1],
      opacity: [0.7, 1, 0.7],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 dark:from-gray-900 dark:via-orange-900/20 dark:to-amber-900/10 flex flex-col relative overflow-hidden">
      {/* Floating background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            variants={floatingVariants}
            animate="animate"
            className={`absolute w-2 h-2 bg-orange-400/20 rounded-full
              ${i === 0 ? 'top-1/4 left-1/4' : ''}
              ${i === 1 ? 'top-1/3 right-1/4' : ''}
              ${i === 2 ? 'bottom-1/4 left-1/3' : ''}
              ${i === 3 ? 'top-2/3 right-1/3' : ''}
              ${i === 4 ? 'bottom-1/3 left-1/2' : ''}
              ${i === 5 ? 'top-1/2 right-1/2' : ''}
            `}
            style={{ animationDelay: `${i * 0.5}s` }}
          />
        ))}
      </div>

      {/* Enhanced Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
        className="absolute top-4 left-4 sm:top-6 sm:left-6 z-10"
      >
        <Link
          to="/"
          className="group flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full shadow-lg border border-orange-200/50 dark:border-orange-800/50 text-gray-700 dark:text-gray-200 hover:bg-orange-100 dark:hover:bg-orange-900/50 transition-all duration-300"
        >
          <motion.div
            whileHover={{ x: -2 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <ArrowLeft className="w-4 h-4" />
          </motion.div>
          <span className="text-sm font-medium">Back to Home</span>
        </Link>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md relative">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="relative"
          >
            {/* Glass Card */}
            <div className="relative bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-3xl p-6 sm:p-8 shadow-2xl border border-orange-200/30 dark:border-orange-800/30">
              {/* Success Animation Overlay */}
              <AnimatePresence>
                {success && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-3xl flex items-center justify-center backdrop-blur-sm z-50"
                  >
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 200, damping: 10 }}
                      className="text-green-600 dark:text-green-400"
                    >
                      <Sparkles className="w-16 h-16" />
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Header */}
              <motion.div variants={itemVariants} className="text-center mb-8">
                <motion.div
                  variants={sparkleVariants}
                  animate="animate"
                  className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl mb-6 shadow-lg"
                >
                  <Lock className="w-8 h-8 text-white" />
                </motion.div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  Welcome Back
                </h1>
                <p className="text-gray-600 dark:text-gray-300">
                  Sign in to access your admin dashboard
                </p>
              </motion.div>

              {/* Form */}
              <motion.form variants={itemVariants} onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, x: -20, scale: 0.95 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, x: 20, scale: 0.95 }}
                      className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-600 dark:text-red-400 text-sm backdrop-blur-sm"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-red-500/20 flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-red-500" />
                        </div>
                        {error}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Rate Limit Info */}
                <AnimatePresence>
                  {rateLimitInfo && rateLimitInfo.remaining < 5 && (
                    <motion.div
                      initial={{ opacity: 0, x: -20, scale: 0.95 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, x: 20, scale: 0.95 }}
                      className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-600 dark:text-amber-400 text-sm backdrop-blur-sm"
                    >
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>
                          {rateLimitInfo.remaining} login attempts remaining
                          {rateLimitInfo.resetAt && (
                            <span className="block text-xs mt-1 opacity-75">
                              Resets at {new Date(rateLimitInfo.resetAt).toLocaleTimeString()}
                            </span>
                          )}
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Email Field */}
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Email Address
                  </label>
                  <div className="relative">
                    <motion.div
                      animate={{
                        scale: focusedField === 'email' ? 1.05 : 1,
                        color: focusedField === 'email' ? '#f97316' : '#9ca3af'
                      }}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10"
                    >
                      <Mail className="w-5 h-5" />
                    </motion.div>
                    <motion.input
                      whileFocus={{ scale: 1.02 }}
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      className="w-full pl-12 pr-4 py-4 bg-white/60 dark:bg-gray-700/60 border-2 border-gray-200/50 dark:border-gray-600/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:bg-white dark:focus:bg-gray-700 transition-all duration-300 backdrop-blur-sm"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Password
                  </label>
                  <div className="relative">
                    <motion.div
                      animate={{
                        scale: focusedField === 'password' ? 1.05 : 1,
                        color: focusedField === 'password' ? '#f97316' : '#9ca3af'
                      }}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10"
                    >
                      <Lock className="w-5 h-5" />
                    </motion.div>
                    <motion.input
                      whileFocus={{ scale: 1.02 }}
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setFocusedField('password')}
                      onBlur={() => setFocusedField(null)}
                      className="w-full pl-12 pr-14 py-4 bg-white/60 dark:bg-gray-700/60 border-2 border-gray-200/50 dark:border-gray-600/50 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:bg-white dark:focus:bg-gray-700 transition-all duration-300 backdrop-blur-sm"
                      placeholder="Enter your password"
                      required
                    />
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-orange-500 transition-colors z-10"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </motion.button>
                  </div>
                </div>

                {/* Submit Button */}
                <motion.button
                  whileHover={{ scale: 1.02, boxShadow: "0 10px 30px rgba(249, 115, 22, 0.3)" }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isLoading || success}
                  className="relative w-full py-4 px-6 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-xl shadow-lg hover:from-orange-600 hover:to-amber-600 focus:outline-none focus:ring-4 focus:ring-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 overflow-hidden"
                >
                  <AnimatePresence mode="wait">
                    {isLoading ? (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center justify-center gap-3"
                      >
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Signing you in...</span>
                      </motion.div>
                    ) : success ? (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center justify-center gap-3"
                      >
                        <Sparkles className="w-5 h-5" />
                        <span>Welcome back!</span>
                      </motion.div>
                    ) : (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        Sign In to Dashboard
                      </motion.span>
                    )}
                  </AnimatePresence>

                  {/* Button shine effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '100%' }}
                    transition={{ duration: 0.6 }}
                  />
                </motion.button>
              </motion.form>

              {/* Additional Info */}
              <motion.div variants={itemVariants} className="mt-8 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Admin access required • Secure login
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}